import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Image as ImageIcon, Upload, Trash2, FileText, Download, Filter } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';

const SECTIONS = [
  { key: 'probound', label: 'ProBound', color: '#2F5E96' },
  { key: 'proepoxy', label: 'ProEpoxy', color: '#7A0B7A' },
  { key: 'prodecor', label: 'ProDecor', color: '#D4A24B' },
];

export default function MarketingMaterial() {
  const navigate = useNavigate();
  const { isStaff } = useContext(AuthContext);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ section: 'probound', title: '', description: '', file: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/marketing-assets${filter ? `?section=${filter}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load assets');
      setAssets(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!isStaff) { navigate('/'); return; }
    (async () => { await load(); })();
  }, [isStaff, navigate, load]);

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!form.file) { toast.error('Choose a file'); return; }
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('section', form.section);
      fd.append('title', form.title);
      fd.append('description', form.description);
      const res = await fetch(`${API_URL}/api/admin/marketing-assets`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Upload failed');
      toast.success('Uploaded');
      setForm({ section: form.section, title: '', description: '', file: null });
      const fileInput = document.getElementById('mm-file');
      if (fileInput) fileInput.value = '';
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (asset) => {
    if (!window.confirm(`Delete "${asset.title}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/marketing-assets/${asset.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const isImage = (t) => ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes((t || '').toLowerCase());

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #7A0B7A 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/staff')} className="p-2 hover:bg-white/10 rounded-lg" data-testid="mm-back" aria-label="Back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <ImageIcon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#D4A24B' }} />
              Marketing Material
            </h1>
            <p className="hidden sm:block text-white/70 text-xs sm:text-sm mt-0.5">
              Upload assets per section. Installers only see assets for sections they have access to.
            </p>
          </div>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="mm-home"><Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Staff</span></Button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-5 grid lg:grid-cols-[340px_1fr] gap-4">
        {/* Upload form */}
        <Card className="p-4 h-fit">
          <h2 className="font-bold text-sm uppercase tracking-wider text-gray-700 mb-3 flex items-center gap-2">
            <Upload className="w-4 h-4" /> Upload New Asset
          </h2>
          <form onSubmit={submitUpload} className="space-y-3" data-testid="mm-upload-form">
            <div>
              <Label className="text-xs font-semibold">Section</Label>
              <div className="grid grid-cols-3 gap-1 mt-1">
                {SECTIONS.map((s) => (
                  <button
                    type="button"
                    key={s.key}
                    data-testid={`mm-section-${s.key}`}
                    onClick={() => setForm({ ...form, section: s.key })}
                    className={`text-xs font-semibold py-1.5 rounded-md border-2 transition-colors ${
                      form.section === s.key ? 'text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                    style={form.section === s.key ? { background: s.color, borderColor: s.color } : {}}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold" htmlFor="mm-title">Title</Label>
              <Input id="mm-title" data-testid="mm-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Spring 2026 Brochure" className="h-9" />
            </div>
            <div>
              <Label className="text-xs font-semibold" htmlFor="mm-desc">Description (optional)</Label>
              <Textarea id="mm-desc" data-testid="mm-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="text-sm" />
            </div>
            <div>
              <Label className="text-xs font-semibold" htmlFor="mm-file">File (max 50 MB)</Label>
              <input
                id="mm-file"
                data-testid="mm-file"
                type="file"
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                className="block w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#7A0B7A]/10 file:text-[#7A0B7A] hover:file:bg-[#7A0B7A]/20"
              />
              <p className="text-[10px] text-gray-500 mt-1">PDF, JPG, PNG, WebP, MP4, DOCX, XLSX, PPTX, ZIP supported.</p>
            </div>
            <Button type="submit" disabled={uploading} className="w-full bg-[#7A0B7A] hover:bg-[#5a085a] text-white" data-testid="mm-submit">
              <Upload className="w-4 h-4 mr-1.5" /> {uploading ? 'Uploading…' : 'Upload Asset'}
            </Button>
          </form>
        </Card>

        {/* Gallery */}
        <div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Filter className="w-4 h-4 text-gray-500" />
            <button data-testid="mm-filter-all" onClick={() => setFilter('')} className={`text-xs px-3 py-1.5 rounded-full border-2 ${filter === '' ? 'border-[#7A0B7A] bg-[#7A0B7A] text-white font-semibold' : 'border-gray-200 bg-white text-gray-600'}`}>All</button>
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                data-testid={`mm-filter-${s.key}`}
                onClick={() => setFilter(s.key)}
                className={`text-xs px-3 py-1.5 rounded-full border-2 ${filter === s.key ? 'text-white font-semibold' : 'border-gray-200 bg-white text-gray-600'}`}
                style={filter === s.key ? { background: s.color, borderColor: s.color } : {}}
              >
                {s.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-500">Loading…</div>
          ) : assets.length === 0 ? (
            <div className="text-center py-16 text-gray-400 italic border-2 border-dashed border-gray-200 rounded-xl">
              No marketing assets yet. Upload one to get started.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="mm-asset-grid">
              {assets.map((a) => {
                const sec = SECTIONS.find((s) => s.key === a.section) || { label: a.section, color: '#666' };
                return (
                  <Card key={a.id} className="overflow-hidden" data-testid={`mm-asset-${a.id}`}>
                    <div className="relative bg-gray-100 aspect-[4/3] flex items-center justify-center">
                      {isImage(a.file_type) ? (
                        <img src={a.file_url} alt={a.title} className="w-full h-full object-cover" />
                      ) : (
                        <FileText className="w-12 h-12 text-gray-400" />
                      )}
                      <span className="absolute top-2 left-2 text-[10px] font-bold uppercase text-white px-2 py-0.5 rounded-full" style={{ background: sec.color }}>
                        {sec.label}
                      </span>
                    </div>
                    <div className="p-3">
                      <h3 className="font-semibold text-sm truncate" title={a.title}>{a.title}</h3>
                      {a.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{a.description}</p>}
                      <p className="text-[10px] text-gray-400 mt-1">{(a.file_size / 1024).toFixed(0)} KB · .{a.file_type}</p>
                      <div className="flex gap-1.5 mt-2">
                        <a href={a.file_url} download target="_blank" rel="noopener noreferrer" className="flex-1">
                          <Button size="sm" variant="outline" className="w-full h-8 text-xs" data-testid={`mm-download-${a.id}`}>
                            <Download className="w-3.5 h-3.5 mr-1" /> Open
                          </Button>
                        </a>
                        <Button size="sm" variant="outline" onClick={() => remove(a)} className="h-8 px-2 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`mm-delete-${a.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
